
import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { SimulationStep } from './types';
import { CheckCircle, Circle, Clock } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';

interface SimulationTimelineProps {
  steps: SimulationStep[];
  currentStep: number;
  progress: number;
}

export const SimulationTimeline = ({ 
  steps, 
  currentStep, 
  progress 
}: SimulationTimelineProps) => {
  const isMobile = useIsMobile();
  
  const getStepStatus = (stepIndex: number) => {
    if (stepIndex < currentStep) return 'completed';
    if (stepIndex === currentStep && progress > 0) return 'current';
    return 'pending';
  };
  
  return (
    <Card>
      <CardContent className="p-3 md:p-4">
        <div className={cn(
          "relative",
          isMobile ? "flex flex-col space-y-3" : "flex space-x-2 items-center"
        )}>
          {isMobile ? (
            // Mobile vertical timeline
            <div className="absolute left-[12px] top-6 bottom-6 w-0.5 bg-muted dark:bg-slate-700"></div>
          ) : (
            // Desktop horizontal timeline
            <div className="absolute left-0 right-0 top-[12px] h-0.5 bg-muted dark:bg-slate-700"></div>
          )}
          
          {steps.map((step, index) => {
            const status = getStepStatus(index);
            
            return (
              <div 
                key={step.id}
                className={cn(
                  "relative flex-1",
                  isMobile ? "flex items-start pl-7" : "flex flex-col items-center pt-7"
                )}
              >
                <div className={cn(
                  "absolute flex items-center justify-center rounded-full w-6 h-6 bg-card",
                  isMobile ? "left-0 top-0" : "top-0 left-1/2 transform -translate-x-1/2",
                  status === 'completed' ? "border-2 border-primary" : 
                  status === 'current' ? "border-2 border-primary" : 
                  "border border-muted-foreground"
                )}>
                  {status === 'completed' ? (
                    <CheckCircle className="h-4 w-4 text-primary" />
                  ) : status === 'current' ? (
                    <Clock className="h-4 w-4 text-primary" />
                  ) : (
                    <Circle className="h-4 w-4 text-muted-foreground" />
                  )}
                </div>
                
                <div>
                  <p className={cn(
                    "text-xs font-medium",
                    status === 'completed' ? "text-primary" : 
                    status === 'current' ? "text-primary" : 
                    "text-muted-foreground"
                  )}>
                    {step.name}
                  </p>
                  {status === 'current' && (
                    <p className="text-xs text-muted-foreground mt-1 max-w-xs">
                      {step.description}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};
